// CompeteTotalForm.jsx — Firestore-wired QR form (no schoolId) + perf tweaks + duplicate-email guard + AGE FIX
// -----------------------------------------------------------------------------
import React, { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Music, Piano, Loader2 } from "lucide-react";

// 🔥 Firestore
import { db } from "../firebase"; // adjust if your path differs
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  limit as fsLimit,
} from "firebase/firestore";

/* ---------------------------------------------
   Required itinerary (exact)
---------------------------------------------- */
const ITINERARY = [
  { date: "Wed, Dec 17, 2025", time: "5:00–9:30 PM", title: "Check-in / Rehearsal", venue: "The DiMenna Center for Classical Music", map: "The DiMenna Center for Classical Music NYC" },
  { date: "Thu, Dec 18, 2025", time: "5:00–8:00 PM", title: "Christmas Concert", venue: "Riverside Church, Manhattan", map: "Riverside Church NYC" },
  { date: "Fri, Dec 19, 2025", time: "9:00 AM–1:00 PM", title: "Judged Competition", venue: "Riverside Church, Manhattan", map: "Riverside Church, NYC" },
  { date: "Fri, Dec 19, 2025", time: "3:00–5:00 PM", title: "Masterclass ", venue: "Riverside Church, Manhattan", map: "Riverside Church NYC" },
  { date: "Fri, Dec 19, 2025", time: "5:00–6:00 PM", title: "Finalist Performance ", venue: "Riverside Church", map: "Riverside Church NYC" },
  { date: "Fri, Dec 19, 2025", time: "8:00–9:30 PM", title: "Award Announcement", venue: "UCMP", map: " United Children's Music Project" },
];

const AWARDS =
  "Awards & results immediately following at 4710 Park Avenue, Weehawken, NJ 07086 • Tel: 201-228-0341 • Fax: 866-644-5542";

/* ---------------------------------------------
   Repertoire by instrument with real age ranges (3–6 minutes; excerpts OK)
   NOTE: Keys must match Instrument <select/> exactly.
---------------------------------------------- */
export const REPERTOIRE = Object.freeze({
  Violin: [
    {
      ageRange: [8, 10],
      title: "Age Group 8-10 (Violin)",
      pieces: [
        "Required Pieces (Suzuki Book No. 2)",
        "Chorus from 'Judas Maccabaeus' — G. F. Handel",
        "Musette — J. S. Bach",
        "Hunters' Chorus — C. M. von Weber",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Age Group 11-14 (Violin)",
      pieces: [
        "Rieding — Violin Concerto No. 2, Op. 35",
        "Seitz — Student Concerto No. 5",
        "Vivaldi — Concerto in A Minor, RV 356 (1st Movement)",
      ],
    },
    {
      ageRange: [15, 20],
      title: "Age Group 15-20 (Violin)",
      pieces: [
        "Bach — Concerto in A Minor, BWV 1041",
        "Accolay — Violin Concerto No. 2 in A Minor",
        "Mozart — Violin Concerto No. 3 in G Major, K.216",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Age Group 21+ (Violin)",
      pieces: [
        "Bruch — Violin Concerto No. 1 in G Minor, Op. 26",
        "Mendelssohn — Violin Concerto in E Minor, Op. 64",
        "Saint-Saëns — Violin Concerto No. 3 in B Minor, Op. 61",
      ],
    },
  ],

  Viola: [
    {
      ageRange: [8, 10],
      title: "Age Group 8-10 (Viola)",
      pieces: [
        "Oskar Rieding – Concerto in E Minor, Op. 35",
        "Seitz – Concerto No. 5, 1st Movement (No. 2 from Suzuki Viola Book 4)",
        "Telemann – Concerto in G, 1st Movement (from Suzuki Viola Book 4)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Age Group 11-14 (Viola)",
      pieces: [
        "Sonata in G Minor – 4th Movement (No. 3 from Suzuki Viola Book 5)",
        "Veracini – Gigue (No. 5 from Suzuki Viola Book 5)",
        "Seitz – Concerto No. 3 in C Minor (No. 9 from Suzuki Viola Book 5)",
      ],
    },
    {
      ageRange: [15, 20],
      title: "Age Group 15-20 (Viola)",
      pieces: [
        "F.A. Hoffmeister – Viola Concerto in D Minor, 1st Allegro with Cadenza",
        "Bach/Casadesus – Concerto for Viola in C Minor",
        "Handel/Casadesus – Viola Concerto, 1st Movement",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Age Group 21+ (Viola)",
      pieces: [
        "Vivaldi's Four Seasons for Viola",
        "Stamitz – Viola Concerto in D Minor, Op. 1, 1st Movement: Risoluto",
        "Vanhal – Viola Concerto in C Major",
      ],
    },
  ],

  Cello: [
    {
      ageRange: [8, 10],
      title: "Age Group 8-10 (Cello)",
      pieces: [
        "F. Seitz – Concerto No. 5, 1st Movement",
        "C. Webster – Scherzo (from Suzuki Cello Book 3)",
        "Vivaldi – Cello Concerto in C Major, RV 399",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Age Group 11-14 (Cello)",
      pieces: [
        "J. B. Bréval – Sonata in C Major, Allegro",
        "Bach – Cello Suite No. 1 in G Major",
        "Goltermann – Rondo: Allegro Molto (performed by Susanne Beer and Gareth Hancock)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Age Group 15-21 (Cello)",
      pieces: [
        "Elgar – Cello Concerto",
        "Haydn – Concerto No. 1 in C Major, Hob. VIIb:1",
        "Jean Baptiste Bréval – Rondo from Concerto No. 2 in D Major",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Age Group 21+ (Cello)",
      pieces: [
        "Dvořák – Cello Concerto",
        "Schumann – Cello Concerto, Op. 129",
        "Barber – Cello Concerto",
      ],
    },
  ],

  Trumpet: [
    {
      ageRange: [8, 10],
      title: "Age Group 8-10 (Trumpet)",
      pieces: [
        "When the Saints Go Marching In",
        "The Beautiful Snow – from the Arban Trumpet Method",
        "Jean-Baptiste Arban – Variations on The Carnival of Venice (1st Movement only)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Age Group 11-14 (Trumpet)",
      pieces: [
        "Arban – Characteristic Study No. 1",
        "Jean-Baptiste Arban – Fantasie Brillante (as performed by Wynton Marsalis, up to the 3rd movement)",
        "Joseph Haydn – Trumpet Concerto (as performed by Timofei Dokshizer on Bb trumpet)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Age Group 15-21 (Trumpet)",
      pieces: [
        "Johann Hummel – Trumpet Concerto (as performed by Sergei Nakariakov on Bb trumpet)",
        "Cascade",
        "Alexander Arutunian – Trumpet Concerto",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Age Group 21+ (Trumpet)",
      pieces: [
        "Oscar Böhme – Trumpet Concerto in F Minor, Op. 18",
        "Henri Tomasi – Trumpet Concerto",
        "André Jolivet – Concerto for Trumpet",
      ],
    },
  ],

  Trombone: [
    {
      ageRange: [8, 10],
      title: "Age Group 8-10 (Trombone)",
      pieces: [
        "When the Saints Go Marching In",
        "The Beautiful Snow – from the Arban Trombone Method",
        "Variations on Twinkle Twinkle Little Star (Simplified Version)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Age Group 11-14 (Trombone)",
      pieces: [
        "Arban – Characteristic Study No. 1 (Trombone Adaptation)",
        "Arban – Fantasie Brillante (up to the final variation)",
        "Galliard – Sonata No. 1 in A Minor (1st or 2nd Movement)",
        "Larsson – Concertino for Trombone, Op. 45 No. 7 (1st Movement)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Age Group 15-21 (Trombone)",
      pieces: [
        "Ferdinand David – Concertino for Trombone, Op. 4",
        "Camille Saint-Saëns – Cavatine, Op. 144",
        "Launy Grøndahl – Trombone Concerto",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Age Group 21+ (Trombone)",
      pieces: [
        "Henri Tomasi – Trombone Concerto",
        "Nino Rota – Trombone Concerto",
        "Derek Bourgeois – Trombone Concerto",
      ],
    },
  ],

  Tuba: [
    {
      ageRange: [8, 10],
      title: "Age Group 8-10 (Tuba)",
      pieces: [
        "When the Saints Go Marching In",
        "The Beautiful Snow – from the Arban Tuba Method (or similar lyrical etude)",
        "Variations on Twinkle Twinkle Little Star (Simplified Version)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Age Group 11-14 (Tuba)",
      pieces: [
        "Arban – Characteristic Study No. 1 (Tuba Adaptation)",
        "Haddad – Suite for Tuba (1st or 2nd Movement)",
        "Capuzzi – Andante and Rondo (arranged for Tuba)",
        "Marcello – Sonata in F (1st and 2nd Movements)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Age Group 15-21 (Tuba)",
      pieces: [
        "Ralph Vaughan Williams – Concerto for Bass Tuba",
        "Edward Gregson – Tuba Concerto",
        "Lebedev – Concerto in One Movement",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Age Group 21+ (Tuba)",
      pieces: [
        "John Williams – Tuba Concerto",
        "Bruce Broughton – Tuba Concerto",
        "Krzysztof Penderecki – Capriccio for Tuba and Orchestra",
      ],
    },
  ],

  "Double Bass": [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Double Bass)",
      pieces: [
        "Gavotte – Suzuki Bass Book 3",
        "Minuet No. 2 – Suzuki Bass Book 3",
        "Antonín Dvořák – Humoresque (arranged for bass)",
        "J.S. Bach – Minuet in G (Suzuki-style)",
        "Henry Purcell – Rigadoon (arranged for bass)",
      ],
    },
    {
      ageRange: [11, 15],
      title: "Nivel 11 a 15 (Double Bass)",
      pieces: [
        "Tambourin – Suzuki Bass Book 5",
        "Keyper – Romance and Rondo (technically engaging, elegant Classical style)",
        "Capuzzi – Double Bass Concerto in F Major (Suzuki Book 5 or standalone)",
        "Telemann – Sonata in A Minor, 2nd Movement: Allegro (Energetic and more technically demanding than the lyrical 1st movement)",
      ],
    },
    {
      ageRange: [16, 21],
      title: "Nivel 16 a 21 (Double Bass)",
      pieces: [
        "Giovanni Bottesini – Elegy",
        "František Hertl – Sonata for Double Bass and Piano",
        "Keyper – Romance and Rondo (if not placed earlier)",
        "Reinhold Glière – Intermezzo and Tarantella",
        "Adolf Mišek – Sonata No. 2 in E Minor (Powerful, expressive, and technically rich)",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21+ (Double Bass)",
      pieces: [
        "Giovanni Bottesini – Tarantella or Concertos No. 1 or 2",
        "Serge Koussevitzky – Humoresque, Op. 4",
        "Serge Koussevitzky – Double Bass Concerto in F♯ Minor",
        "Hans Fryba – Suite in the Olden Style",
        "Frank Proto – A Carmen Fantasy",
      ],
    },
  ],

  Oboe: [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Oboe)",
      pieces: [
        "Jean-Baptiste Loeillet – Gavotte",
        "James Hook – Minuet in G",
        "Alessandro Marcello – Concierto en Re menor (1er movimiento)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Nivel 11 a 14 (Oboe)",
      pieces: [
        "Georg Philipp Telemann – Fantasie Nº 1 en La Mayor",
        "Camille Saint-Saëns – Sonata para oboe, Op. 166 (1er movimiento)",
        "Franz Wilhelm Ferling – 48 Estudios para oboe, Op. 31 (estudios intermedios)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Nivel 15 a 21 (Oboe)",
      pieces: [
        "Josef Fiala – Concierto para oboe en Si bemol mayor",
        "Francis Poulenc – Sonata para oboe y piano",
        "Benjamin Britten – Six Metamorphoses after Ovid, Op. 49",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (Oboe)",
      pieces: [
        "Richard Strauss – Concierto para oboe en Re mayor",
        "Bohuslav Martinů – Concierto para oboe y pequeña orquesta",
        "Eugène Bozza – Fantaisie Pastorale o Concierto para oboe y cuerdas",
      ],
    },
  ],

  Flute: [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Flute)",
      pieces: [
        "Wilhelm Popp – Little Melody, Op. 384",
        "Giuseppe Gariboldi – Étude Mélodique, Op. 132 No. 1",
        "Jean-Louis Tulou – Petite Étude (from Méthode de flûte)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Nivel 11 a 14 (Flute)",
      pieces: [
        "Georg Philipp Telemann – Sonata in F Major, I. Vivace",
        "Paul Taffanel – Andante Pastoral et Scherzettino",
        "Johann Sebastian Bach – Sonata en Do mayor, BWV 1033 (1er movimiento)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Nivel 15 a 21 (Flute)",
      pieces: [
        "Wolfgang Amadeus Mozart – Concierto para flauta en Re mayor, K. 313 (1er movimiento)",
        "Carl Philipp Emanuel Bach – Sonata en Re menor, H. 514 (1er movimiento)",
        "Philippe Gaubert – Nocturne et Allegro Scherzando",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (Flute)",
      pieces: [
        "Francis Poulenc – Sonata para flauta y piano",
        "Georges Enesco – Concertstück para flauta y orquesta",
        "Carl Reinecke – Concerto in D Major, Op. 283",
      ],
    },
  ],

  Clarinet: [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Clarinet)",
      pieces: [
        "Carl Baermann – Vocalise, Op. 63 No. 3",
        "Gustav Langenus – First Solo (from First Solo Album for Clarinet)",
        "James Rae – Easy Study No. 3 (from 18 Concert Studies for Clarinet)",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Nivel 11 a 14 (Clarinet)",
      pieces: [
        "Carl Baermann – Clarinet Method, Book 3 (selected progressive pieces)",
        "Carl Stamitz – Concerto No. 3 in B-flat Major (1er movimiento)",
        "Fritz Kreisler – Liebesfreud (arr. para clarinete)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Nivel 15 a 21 (Clarinet)",
      pieces: [
        "Carl Maria von Weber – Concertino for Clarinet, Op. 26",
        "Camille Saint-Saëns – Sonata para clarinete y piano, Op. 167",
        "Paul Jeanjean – Arabesques",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (Clarinet)",
      pieces: [
        "Carl Nielsen – Clarinet Concerto",
        "Johannes Brahms – Sonata para clarinete en Fa menor, Op. 120 No. 1",
        "Henri Tomasi – Clarinet Concerto",
      ],
    },
  ],

  Bassoon: [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Bassoon)",
      pieces: [
        "Ludwig Milde – Study No. 1, from 25 Studies in Scales and Chords, Op. 24",
        "Julius Weissenborn – Study No. 6, from Practical Method for Bassoon, Op. 8 Part I",
        "Julius Weissenborn – Study No. 1, from Practical Method for Bassoon, Op. 8 Part I",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Nivel 11 a 14 (Bassoon)",
      pieces: [
        "Ferdinand David – Concertino, Op. 12",
        "Ludwig Milde – Concert Studies, Op. 26 No. 1",
        "Joseph Bodin de Boismortier – Sonata in A minor, Op. 14 No. 5 (1er movimiento)",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Nivel 15 a 21 (Bassoon)",
      pieces: [
        "Carl Maria von Weber – Andante e Rondo Ungarese, Op. 35",
        "Camille Saint-Saëns – Sonata para fagot y piano, Op. 168",
        "Georg Philipp Telemann – Sonata in F Minor, IV. Vivace",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (Bassoon)",
      pieces: [
        "Wolfgang Amadeus Mozart – Concierto para fagot en Si bemol mayor, K. 191",
        "André Jolivet – Concerto pour basson et orchestre à cordes",
        "Paul Hindemith – Sonata para fagot y piano",
      ],
    },
  ],

  Piano: [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Piano)",
      pieces: [
        "Johann Sebastian Bach – Minuet in G Major, BWV Anh. 114",
        "Ludwig van Beethoven – Ecossaise in G Major, WoO 23",
        "Cornelius Gurlitt – Album for the Young, Op. 140 No. 3",
      ],
    },
    {
      ageRange: [11, 14],
      title: "Nivel 11 a 14 (Piano)",
      pieces: [
        "Friedrich Burgmüller – Arabesque, Op. 100 No. 2",
        "Robert Schumann – The Wild Horseman, Op. 68 No. 8",
        "Dmitri Kabalevsky – Clowns, Op. 39 No. 20",
      ],
    },
    {
      ageRange: [15, 21],
      title: "Nivel 15 a 21 (Piano)",
      pieces: [
        "Ludwig van Beethoven – Sonatina in G Major, Anh. 5 No. 1",
        "Frédéric Chopin – Waltz in A Minor, B. 150",
        "Erik Satie – Gymnopédie No. 1",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (Piano)",
      pieces: [
        "Ludwig van Beethoven – Sonata No. 14 “Claro de Luna” (Moonlight), Op. 27 No. 2",
        "Frédéric Chopin – Nocturne in E-flat Major, Op. 9 No. 2",
        "Claude Debussy – Clair de Lune, from Suite Bergamasque",
      ],
    },
  ],

  "French Horn": [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (French Horn)",
      pieces: [
        "Russian Dance – Kopprasch / Poole",
        "Nocturne, Op. 35, No. 10 – Gliere",
        "Pavane Pour Infante Défunte – Ravel / Dishinger",
        "Allerseelen – Strauss / Voxman",
      ],
    },
    {
      ageRange: [11, 15],
      title: "Nivel 11 a 15 (French Horn)",
      pieces: [
        "Nocturno, Op. 7 – Strauss, F.",
        "Concerto No. 2 in E♭ (mvt. 2) – Strauss, R.",
        "Concerto No. 2, K. 417 (1st mvt.) – Mozart",
        "Concerto No. 3, K. 447 (1st mvt.) – Mozart",
        "Concerto No. 4, K. 495 (1st mvt.) – Mozart",
      ],
    },
    {
      ageRange: [16, 21],
      title: "Nivel 16 a 21 (French Horn)",
      pieces: [
        "Hunter’s Moon – Vinter, G.",
        "Concerto No. 1 in E♭, Op. 11 (mvt. 1 or 2 & 3) – Strauss",
        "Morceau De Concert – Saint Saens",
        "Concertpiece, Op. 94 – Saint Saens",
        "Laudatio (unaccompanied) – Krol, Bernhard",
        "Sonata for Horn and Piano – Heiden, Bernhard",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (French Horn)",
      pieces: [
        "Adagio and Allegro, Op. 70 – Schumann",
        "Concerto in B♭ Major, Op. 91 (Any Mvt.) – Gliere / Polekh",
        "Villanelle – Dukas",
        "Parable VIII, Op. 120 (unaccompanied) – Persichetti, Vincent",
        "Horn Call (Horn-Lokk) – Berge, Sigurd (unaccompanied)",
      ],
    },
  ],

  "Percusión / Teclados": [
    {
      ageRange: [8, 10],
      title: "Nivel 8 a 10 (Percusión / Teclados)",
      pieces: [
        "Ayodhya Vasi Ram",
        "The Juggler – George Hamilton Green",
        "El vuelo del abejorro – Nikolái Rimski-Kórsakov",
      ],
    },
    {
      ageRange: [11, 15],
      title: "Nivel 11 a 15 (Percusión / Teclados)",
      pieces: [
        "Concierto en La menor – Antonio Vivaldi",
        "Cuarto movimiento de la Sinfonía No. 4 – Piotr Ilich Chaikovski (redoblante)",
        "El vuelo del abejorro – Nikolái Rimski-Kórsakov",
      ],
    },
    {
      ageRange: [16, 21],
      title: "Nivel 16 a 21 (Percusión / Teclados)",
      pieces: [
        "The Solo Snare Drummer, Lección N.º 2 – Vic Firth",
        "Hungarian Dance No. 5 – Johannes Brahms",
        "Concierto para violín en La menor – Johann Sebastian Bach (adaptación para xilófono, 1er movimiento)",
      ],
    },
    {
      ageRange: [21, Infinity],
      title: "Nivel 21 en adelante (Percusión / Teclados)",
      pieces: [
        "Ragtime Potpourri: The Golden Age of the Xylophone",
        "Whispering Rivers – Elliott Fogo",
        "Etude in E Minor – Casey Cangelosi",
        "Children’s Corner – Claude Debussy",
      ],
    },
  ],
});

// Instruments list for the <select/>
const ALL_INSTRUMENTS = Object.freeze(Object.keys(REPERTOIRE));

/* ---------------------------------------------
   Concert Sheet Music folders (Google Drive)
---------------------------------------------- */
const MUSIC_FOLDERS = Object.freeze({
  Violin: [
    { label: "Violin I", url: "https://drive.google.com/drive/folders/1Ml3YF9192Ah2D-yT8bZTqorHBJbuR62B?usp=sharing" },
    { label: "Violin II", url: "https://drive.google.com/drive/folders/1yeX12_QG8pK7rI4Lik2Xn8dM-XexT08M?usp=sharing" },
  ],
  Viola: [{ label: "Viola", url: "https://drive.google.com/drive/folders/1dWwiGMIXVW1svsjMCGDC1NT4J2s79NmV?usp=sharing" }],
  Cello: [{ label: "Cello", url: "https://drive.google.com/drive/folders/1SMOTUVtVBqIrgdAdyxexcXK44Bxqb117?usp=sharing" }],
  "Double Bass": [{ label: "Bass", url: "https://drive.google.com/drive/folders/1c0EIIdCjayQ02f5CS8Y_HrM6eaetjJNA?usp=share_link" }],
  Flute: [{ label: "Flute", url: "https://drive.google.com/drive/folders/1_e6dr6Hu_W5pBaYMp_euMVP_fic1FH41?usp=sharing" }],
  Clarinet: [{ label: "Clarinet", url: "https://drive.google.com/drive/folders/1H50VwGSnSQYZ4Ptl35yFbZinKIrJQA8U?usp=sharing" }],
  Oboe: [{ label: "Oboe", url: "https://drive.google.com/drive/folders/1SUfrHfzkSgj7cPJVEW-UCZyLh7rlQ_75?usp=sharing" }],
  Bassoon: [{ label: "Bassoon", url: "https://drive.google.com/drive/folders/1Hdk0AmBnygPeKOCqEmwJ-SJfwda3bo5F?usp=sharing" }],
  Trumpet: [{ label: "Trumpet", url: "https://drive.google.com/drive/folders/1KJn67-D3qr7S8chOxgte0V9A8nYxL0Tx?usp=sharing" }],
  Trombone: [{ label: "Trombone", url: "https://drive.google.com/drive/folders/1b4L14Kd7cT028T61xlJ4FzkyP5BJ9bxM?usp=sharing" }],
  "French Horn": [{ label: "Horn", url: "https://drive.google.com/drive/folders/1kQhgdDOx-XKvP8iaI5G_DZItOV9pDYJZ?usp=sharing" }],
  Tuba: [{ label: "Tuba", url: "https://drive.google.com/drive/folders/1ei7lbVjqUsw2YRl25LIOxyuzFb58RnK6?usp=sharing" }],
  Harp: [{ label: "Harp", url: "https://drive.google.com/drive/folders/your_harp_folder_link?usp=share_link" }], // TODO: replace when ready
  Piano: [{ label: "Piano", url: "https://drive.google.com/drive/folders/1iu1VTL0OumgQhAE7-NHYY1PDLLJJh_Sk?usp=sharing" }],
});

function getMusicLinks(instrument) {
  return MUSIC_FOLDERS[instrument] ?? [];
}

/* ---------------------------------------------
   Helpers for age-based repertoire lookup
---------------------------------------------- */
function getRepertoireGroupByAge(instrument, age) {
  const groups = REPERTOIRE[instrument] || [];
  if (!groups.length) return null;
  // Minimum age guard
  const minAge = groups[0].ageRange[0];
  if (Number(age) < minAge) {
    return { tooYoung: true, minAge };
  }
  return (
    groups.find((g) => Number(age) >= g.ageRange[0] && Number(age) <= g.ageRange[1]) ||
    null
  );
}

const COLLECTION_NAME = "externalMusicians"; // Firestore collection (auto-ID, no space)
const COMPETITION_NAME = "NY Tour Grand Competition 2025"; // used in dedupe

export default function CompeteTotalForm() {
  const prefersReducedMotion = useReducedMotion();

  // single inline form (no modal)
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    school: "",
    age: "",            // ✅ numeric age replaces ageGroup
    instrument: "",
    chosenPiece: "",
    willAttend: false,
  });
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Suggested repertoire based on instrument + numeric age
  const repGroup = useMemo(() => {
    if (!form.instrument || !form.age) return null;
    return getRepertoireGroupByAge(form.instrument, form.age);
  }, [form.instrument, form.age]);

  const suggestions = useMemo(() => {
    if (!repGroup || repGroup.tooYoung) return [];
    return repGroup?.pieces ?? [];
  }, [repGroup]);

  // Concert music links for the selected instrument
  const musicLinks = useMemo(() => {
    if (!form.instrument) return [];
    return getMusicLinks(form.instrument);
  }, [form.instrument]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.firstName || !form.lastName) return "Name is required.";
    if (!form.email) return "Email is required.";
    if (!form.school) return "Please enter your school.";
    if (!form.age || Number.isNaN(Number(form.age))) return "Please enter a valid age.";
    if (!form.instrument) return "Please select your instrument.";
    if (!form.chosenPiece) return "Please enter your chosen piece for the competition.";
    if (!form.willAttend)
      return "Please confirm you will attend the Dec 17 rehearsal and Dec 18 concert (required).";
    // Too young check
    if (repGroup?.tooYoung) return `You must be at least ${repGroup.minAge} years old to compete on ${form.instrument}.`;
    // No matching group (unlikely unless age < min or <data issue>)
    if (!repGroup) return "We couldn't match your age to a repertoire group. Please check your age.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) return setError(v);
    setError("");
    setSubmitting(true);

    try {
      // 🔐 Duplicate-email guard (per competition)
      const q = query(
        collection(db, COLLECTION_NAME),
        where("email", "==", form.email.trim().toLowerCase()),
        where("competition", "==", COMPETITION_NAME),
        fsLimit(1)
      );
      const existing = await getDocs(q);
      if (!existing.empty) {
        setError("It looks like you already applied with this email. If this is a mistake, contact us.");
        setSubmitting(false);
        return;
      }

      await addDoc(collection(db, COLLECTION_NAME), {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.trim().toLowerCase(),
        school: form.school,
        age: Number(form.age), // ✅ store numeric age
        instrument: form.instrument,
        chosenPiece: form.chosenPiece,
        // Helpful to store which group we matched:
        ageGroupTitle: repGroup?.title ?? null,
        willAttend: form.willAttend,
        competition: COMPETITION_NAME,
        fee: 0,
        source: "ny-tour-qr",
        submittedAt: serverTimestamp(),
      });

      setSent(true);
      // Keep form mounted; just reset values
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        school: "",
        age: "",
        instrument: "",
        chosenPiece: "",
        willAttend: false,
      });
    } catch (err) {
      console.error("[Firestore] Submit error:", err);
      setError("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="competition"
      className="relative py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white overflow-hidden"
    >
      {/* soft wave */}
      <svg className="absolute top-0 left-0 w-full text-gray-900 fill-current" viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path d="M0,10 C120,30 240,60 360,60 C480,60 600,30 720,20 C840,10 960,20 1080,50 C1200,80 1320,110 1440,120 L1440,0 L0,0 Z" />
      </svg>

      {/* decorative icons (disabled if user prefers reduced motion) */}
      {!prefersReducedMotion && (
        <>
          <motion.div
            key="icon-music"
            className="absolute top-10 left-10 opacity-10 select-none pointer-events-none will-change-transform transform-gpu"
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          >
            <Music className="w-12 h-12" />
          </motion.div>
          <motion.div
            key="icon-piano"
            className="absolute bottom-10 right-10 opacity-10 select-none pointer-events-none will-change-transform transform-gpu"
            initial={{ rotate: 0 }}
            animate={{ rotate: -360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          >
            <Piano className="w-14 h-14" />
          </motion.div>
        </>
      )}

      {/* header + prizes */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-4">NY Tour Grand Competition</h2>
        <p className="text-xl mb-12 font-semibold">
          Unleash your talent and step into the spotlight! Win incredible prizes and the chance to be a soloist in our Japan 2026 Tour.
        </p>

        <div className="flex justify-center items-end gap-6 flex-wrap">
          <div className="w-44 bg-white/10 p-4 rounded-xl shadow-xl border-t-4 border-yellow-400">
            <h3 className="text-xl font-bold text-yellow-400">🥈 2nd Place</h3>
            <p>$700 + 10% Off</p>
            <p className="text-sm">All 2026 SimanOF Tours</p>
          </div>
          <div className="relative w-56 bg-white/10 p-6 rounded-xl shadow-xl border-t-4 border-yellow-500">
            <div className="absolute -top-12 w-72 h-72 rounded-full bg-yellow-400 opacity-30 blur-xl z-[-1]" />
            <h3 className="text-2xl font-extrabold text-yellow-500">🥇 1st Place</h3>
            <p className="text-3xl font-extrabold text-green-400 mt-2">$2,200</p>
            <p className="text-md font-semibold mt-1">+ 10% Off All 2026 SimanOF Tours</p>
            <p className="text-sm mt-1">Soloist in Japan 2026</p>
          </div>
          <div className="w-44 bg-white/10 p-4 rounded-xl shadow-xl border-t-4 border-yellow-300">
            <h3 className="text-xl font-bold text-yellow-300">🥉 3rd Place</h3>
            <p>$100 + 10% Off</p>
            <p className="text-sm">All 2026 SimanOF Tours</p>
          </div>
        </div>

        <p className="mt-8 text-sm text-gray-300 max-w-xl mx-auto leading-relaxed">
          <strong>No application fee.</strong> Attendance at the <strong>Dec 17 rehearsal</strong> and the <strong>Dec 18 concert</strong> is required and counts toward competition results.
        </p>
      </div>

      {/* itinerary + SINGLE inline form */}
      <div className="relative z-10 text-left mt-16 max-w-3xl mx-auto px-4">
        <h3 className="text-2xl font-bold mb-3">Required Itinerary</h3>
        <ol className="space-y-3">
          {ITINERARY.map((s, i) => (
            <li key={`it-${i}`} className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
              <p className="text-sm text-white/70">{s.date} • {s.time}</p>
              <p className="font-semibold">{s.title}</p>
              <p className="text-white/90">
                {s.venue} &middot{" "}
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(s.map)}`}
                  className="underline text-yellow-300"
                  target="_blank"
                  rel="noreferrer"
                >
                  Map
                </a>
              </p>
            </li>
          ))}
        </ol>
        <p className="text-xs text-white/60 mt-3">{AWARDS}</p>

        <h3 className="text-2xl font-bold mt-10 mb-1">Apply</h3>
        <p className="text-sm text-white/70 mb-4">
          <strong>Concert attire:</strong> concert black.
        </p>

        {/* success alert stays mounted in DOM for stability */}
        <div aria-live="polite" className="mb-4">
          {sent && (
            <div className="rounded-xl bg-green-600/15 ring-1 ring-green-600/30 p-4 text-green-200">
              Thank you! Your application was received. We’ll follow up by email.
              <button
                type="button"
                onClick={() => setSent(false)}
                className="ml-3 underline text-green-300 hover:text-green-200"
              >
                Submit another
              </button>
            </div>
          )}
        </div>

        {/* keep form mounted; just visually dim when sent */}
        <form onSubmit={handleSubmit} className={`space-y-6 ${sent ? 'opacity-40 pointer-events-none select-none' : ''}`} aria-busy={submitting}>
          {error && (
            <div className="rounded-xl bg-red-600/15 ring-1 ring-red-600/30 p-3 text-red-200">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm opacity-90">First name</span>
              <input
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                autoComplete="given-name"
                required
              />
            </label>
            <label className="block">
              <span className="text-sm opacity-90">Last name</span>
              <input
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                autoComplete="family-name"
                required
              />
            </label>

            <label className="block">
              <span className="text-sm opacity-90">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                autoComplete="email"
                inputMode="email"
                required
              />
            </label>

            <label className="block">
              <span className="text-sm opacity-90">School</span>
              <input
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.school}
                onChange={(e) => update("school", e.target.value)}
                autoComplete="organization"
                required
              />
            </label>

            <label className="block">
              <span className="text-sm opacity-90">Age</span>
              <input
                type="number"
                min="1"
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.age}
                onChange={(e) => update("age", e.target.value)}
                required
              />
            </label>

            <label className="block">
              <span className="text-sm opacity-90">Instrument</span>
              <select
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.instrument}
                onChange={(e) => update("instrument", e.target.value)}
                required
              >
                <option value="">Choose your instrument…</option>
                {ALL_INSTRUMENTS.map((inst, i) => (
                  <option key={`inst-${i}`} value={inst}>{inst}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Dynamic per-instrument + age repertoire */}
          {form.instrument && form.age && repGroup && !repGroup.tooYoung && suggestions.length > 0 && (
            <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
              <p className="font-semibold">
                Suggested repertoire for <span className="underline">{form.instrument}</span>{" "}
                — <span className="underline">{repGroup.title}</span> (3–6 minutes)
              </p>
              <ul className="list-disc list-inside mt-2 opacity-90">
                {suggestions.map((s, i) => (
                  <li key={`sug-${form.instrument}-${i}`}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Too young message */}
          {form.instrument && form.age && repGroup?.tooYoung && (
            <div className="rounded-xl bg-yellow-500/10 ring-1 ring-yellow-500/30 p-4">
              <p className="text-yellow-200">
                You must be at least {repGroup.minAge} years old to compete on {form.instrument}.
              </p>
            </div>
          )}

          {/* Concert sheet music links (appear when instrument is chosen) */}
          {form.instrument && musicLinks.length > 0 && (
            <div className="rounded-xl bg-emerald-600/10 ring-1 ring-emerald-500/30 p-4">
              <p className="font-semibold text-emerald-300">
                Download the concert music for <span className="underline">{form.instrument}</span>
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {musicLinks.map((link, i) => (
                  <a
                    key={`ml-${form.instrument}-${i}`}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 px-3 py-1.5 ring-1 ring-emerald-500/40"
                  >
                    <span className="text-sm font-medium">{link.label}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 4.5h6m0 0v6m0-6L10.5 13.5M6 6v12a2.25 2.25 0 002.25 2.25h9.75" />
                    </svg>
                  </a>
                ))}
              </div>
              {form.instrument === "Violin" && (
                <p className="text-xs text-white/70 mt-2">
                  Not sure which part? Start with your teacher’s assignment. If undecided, download both and bring to rehearsal.
                </p>
              )}
            </div>
          )}

          {/* In case no folder exists for the selected instrument */}
          {form.instrument && musicLinks.length === 0 && (
            <div className="rounded-xl bg-yellow-500/10 ring-1 ring-yellow-500/30 p-4">
              <p className="text-medium text-yellow-300">
                Concert music folder for <span className="underline">{form.instrument}</span> is coming soon. Please check back, or contact us if you need it urgently.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            <label className="block">
              <span className="text-sm opacity-90">Chosen piece for the competition</span>
              <input
                className="mt-1 w-full rounded-lg bg-black/40 ring-1 ring-white/15 px-3 py-2"
                value={form.chosenPiece}
                onChange={(e) => update("chosenPiece", e.target.value)}
                required
              />
            </label>
          </div>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={form.willAttend}
              onChange={(e) => update("willAttend", e.target.checked)}
              className="mt-1 h-4 w-4"
              required
            />
            <span className="text-sm opacity-90">
              I will attend the Dec 17 rehearsal and the Dec 18 concert. I understand attendance is required and counts toward the competition results.
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-yellow-500 text-black px-5 py-2 font-semibold hover:bg-yellow-400 disabled:opacity-60 disabled:cursor-not-allowed transition inline-flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>Submit Application (No Fee)</>
            )}
          </button>
        </form>
      </div>

      {/* bottom wave */}
      <svg className="absolute bottom-0 left-0 w-full text-gray-900 fill-current" viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path d="M0,10 C120,30 240,60 360,60 C480,60 600,30 720,20 C840,10 960,20 1080,50 C1200,80 1320,110 1440,120 L1440,0 L0,0 Z" />
      </svg>
    </section>
  );
}
